import React, { useState } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import {
  ButtonComp,
  DropDownComp,
  ModalCompAdvanceUpdate,
  TableCompData,
  CardComp,
} from "../../../../components";
import FilterFind from "../../../../components/FilterFind";
import EmptyTable from "../../../../components/Common/EmptyTable";
import {
  useDeleteLoanTypeMutation,
  useGetLoanTypesQuery,
} from "../../../../store/admin/loan";
import ImportLoanTypesModal from "./ImportLoanTypesModal";
import { formatMoney } from "../../../../utils";
import { convertToTitleCase } from "../../../../utils/helpers";
import Message from "../../../../components/MessageModal";
import toast from "react-hot-toast";
import TablePagination from "../../../../components/Table/Pagination";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";

const LoanTypePage = ({ setPageName, setDetailsToEdit, detailsToEdit }) => {
  const [modalPage, setModalPage] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const [deleteLoanType, { isLoading: isDeleting }] =
    useDeleteLoanTypeMutation();

  const handleDeleteLoanType = async () => {
    const response = await deleteLoanType({ loanTypeId: detailsToEdit?.id });
    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occured");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occured");
    } else {
      toast.success(data?.message || "Success");
      setModalPage();
    }
  };

  const useModal = [
    {
      name: "delete_loan_type_modal",
      component: (
        <Message
          title={"Confirm Action"}
          loading={isDeleting}
          desc={"Are  you sure you want to delete this loan type?"}
          onBack={() => setModalPage()}
          onProceed={() => handleDeleteLoanType()}
        />
      ),
    },
    {
      name: "import_loan_types_modal",
      component: (
        <ImportLoanTypesModal
          handleClose={() => setModalPage()}
        />
      ),
    },
  ];
  const TableDropDown = (row) => {
    const dropdown = [
      {
        name: "Edit",
        action: (data) => {
          setPageName("edit_loan_type");
          setDetailsToEdit(data);
        },
      },
      {
        name: "Delete",
        action: (data) => {
          setModalPage("delete_loan_type_modal");
          setDetailsToEdit(data);
        },
      },
    ];
    return dropdown;
  };
  const column = [
    {
      name: "S/N",
      selector: (row, index) => (
        <span className="text-muted fw-2" style={{ fontSize: "14px" }}>
          {index + 1}
        </span>
      ),
      width: "80px",
      sortable: false,
    },
    {
      name: "Loan Name",
      selector: (row) => (
        <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
          {row?.name || "N/A"}
        </span>
      ),
      minWidth: "200px",
      sortable: true,
    },
    {
      name: "Loan Model",
      selector: (row) => (
        <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
          {convertToTitleCase(row?.loan_model || "formal")}
        </span>
      ),
      width: "120px",
      sortable: false,
    },
    {
      name: "Interest Rate",
      selector: (row) => (
        <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
          {`${row?.rate || 0}%`}
        </span>
      ),
      width: "140px",
      sortable: true,
      right: true,
    },
    {
      name: "Guarantor Count",
      selector: (row) => (
        <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
          {`${row?.min_guarantor || 0} - ${row?.max_guarantor || 0}`}
        </span>
      ),
      width: "160px",
      sortable: false,
      center: true,
    },
    {
      name: "Loan Amount Range",
      selector: (row) => (
        <div className="d-flex flex-column">
          <span className="fw-2 text-black1" style={{ fontSize: "14px" }}>
            Min: {formatMoney(row?.min_loan_amount || 0)}
          </span>
          <span className="fw-2 text-muted" style={{ fontSize: "12px" }}>
            Max: {formatMoney(row?.max_loan_amount || 0)}
          </span>
        </div>
      ),
      minWidth: "220px",
      sortable: false,
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="text-center">
          <DropDownComp
            dropLabelClassName={"bg-white border-0 px-3 py-1 rounded-4 text-2"}
            arrayComp={TableDropDown(row)}
            ItemClassName="pe-5 text-2"
            input="name"
            row={row}
            DropDownText={
              <span style={{ cursor: "pointer" }}>
                <BsThreeDotsVertical size={20} className="table-action-icon" />
              </span>
            }
          />
        </div>
      ),
      width: "100px",
      center: true,
      sortable: false,
    },
  ];

  const { data, isLoading } = useGetLoanTypesQuery({ page: pageNumber });
  const tableData = data?.data?.configs || [];
  const pagination = data?.data.pagination || {};

  // Filter tableData by search term (case-insensitive)
  const filteredTableData = searchTerm
    ? tableData.filter((row) =>
        row?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : tableData;

  return (
    <section className="px-lg-3 min-vh-100">
      <FilterFind
        title={"Loan Type"}
        onChange={(e) => setSearchTerm(e.target.value)}
        rightSide={true}
        rightSideComponent={
          <div className="d-flex gap-2">
            <ButtonComp
              btnText="Import Loan Types"
              onClick={() => setModalPage("import_loan_types_modal")}
              btnClassName="bg-white py-2 px-4 rounded text-primary1 border-2"
              style={{
                minWidth: "160px",
                borderColor: "var(--color-primary)",
                color: "var(--color-primary)",
              }}
            />
            <ButtonComp
              btnText="Create Loan Type"
              onClick={() => setPageName("create_loan_type")}
              btnClassName="bg-primary1 py-2 px-4 rounded text-white border-0"
              style={{ minWidth: "180px" }}
            />
          </div>
        }
      />

      <CardComp cardClassName={"border-0 rounded-10 px-0 mb-4 pt-4 shadow3"}>
        <TableCompData
          marginBottom={"72px"}
          columns={column}
          data={filteredTableData}
          progressPending={isLoading}
          progressComponent={
            <TableSkeletonLoader rows={10} columns={6} />
          }
          message={
            !isLoading && (
              <EmptyTable
                Message={"No loan type available"}
                btnText={"Create loan type"}
                onClick={() => setPageName("create_loan_type")}
              />
            )
          }
          cellPadding="12px"
        />
      </CardComp>

      {!isLoading && filteredTableData?.length > 0 && (
        <TablePagination
          marginTop={"0"}
          handlePageChange={setPageNumber}
          {...pagination}
        />
      )}

      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => setModalPage()}
        centered
        size={modalPage === "import_loan_types_modal" ? "lg" : "md"}
      />
    </section>
  );
};

export default LoanTypePage;
